import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { apiFetch, setServoToken } from '@/lib/api-client';

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  picture: string | null;
  roles: string[];
}

type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated' | 'forbidden';

interface AuthContextValue {
  status: AuthStatus;
  user: AdminUser | null;
  firebaseUser: User | null;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface ServoLoginResponse {
  user: {
    token: string;
    id: string;
    email: string;
    name: string | null;
    avatar: string | null;
    roles: string[];
  };
}

/**
 * Exchanges a Firebase ID token for a Servo JWT via POST /api/auth/login,
 * then enforces the SUPERADMIN gate.
 */
async function exchangeAndGate(firebaseToken: string): Promise<AdminUser> {
  const res = await apiFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ firebaseToken }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Login failed (${res.status}): ${text || res.statusText}`);
  }

  const data = (await res.json()) as ServoLoginResponse;
  const { token, id, email, name, avatar, roles } = data.user;

  if (!roles?.includes('SUPERADMIN')) {
    throw new Error('Not authorized — admin.servo.music is for Servo SUPERADMINs only.');
  }

  setServoToken(token);
  return { id, email, name, picture: avatar, roles };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AdminUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      if (!fbUser) {
        setServoToken(null);
        setUser(null);
        setStatus('unauthenticated');
        setError(null);
        return;
      }
      try {
        const firebaseToken = await fbUser.getIdToken();
        const adminUser = await exchangeAndGate(firebaseToken);
        setUser(adminUser);
        setStatus('authenticated');
        setError(null);
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Authorization check failed';
        setServoToken(null);
        setError(message);
        setUser(null);
        setStatus('forbidden');
        await signOut(auth).catch(() => {});
      }
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      firebaseUser,
      error,
      signIn: async () => {
        setError(null);
        await signInWithPopup(auth, googleProvider);
      },
      signOut: async () => {
        setServoToken(null);
        await signOut(auth);
      },
    }),
    [status, user, firebaseUser, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
